import axios from "axios";
import { useSnackbar } from "notistack";

const isLoggedin = () => {
  return (
    typeof window?.localStorage !== "undefined" &&
    !!localStorage.getItem("ExanGridToken")
  );
};

// Toast configuration for different HTTP status codes // The below code gives An unexpected error occurred on prod as soon as the user logins in and tries to fetch data. This is because the status code of the response is 200, which is not handled in the getToastConfig function. To fix this, we can add a default case for status codes that are not explicitly handled.
// const getToastConfig = (status: number, message?: string) => {
//   const configs = {
//     201: {
//       variant: "success" as const,
//       message: message || "Success",
//     },
//     204: {
//       variant: "success" as const,
//       message: message || "Success",
//     },
//     400: {
//       variant: "error" as const,
//       message: message || "Something went wrong. Please try again",
//     },
//     401: {
//       variant: "error" as const,
//       message: message || "Session expired. Please log in again",
//     },
//     403: {
//       variant: "error" as const,
//       message: message || "Access denied. You don't have permission to perform this action",
//     },
//     404: {
//       variant: "error" as const,
//       message: message || "Something went wrong. Please try again",
//     },
//     409: {
//       variant: "warning" as const,
//       message: message || "Aleady exists. Please check your data",
//     },
//     422: {
//       variant: "error" as const,
//       message: message || "Validation error. Please check your data",
//     },
//     423: {
//       variant: "error" as const,
//       message: message || "Resricted access. Please contact support",
//     },
//     429: {
//       variant: "warning" as const,
//       message: message || "Too many requests. Please try again later",
//     },
//     500: {
//       variant: "error" as const,
//       message: message || "Server error. Please try again",
//     },
//     502: {
//       variant: "error" as const,
//       message: message || "Service unavailable. Please try again",
//     },
//     503: {
//       variant: "error" as const,
//       message: message || "Service unavailable. Please try again later",
//     },
//     504: {
//       variant: "error" as const,
//       message: message || "Server timeout. Please try again",
//     },
//   };

//   return (
//     configs[status as keyof typeof configs] || {
//       variant: "error" as const,
//       message: message || "An unexpected error occurred",
//     }
//   );
// };

const getToastConfig = (status: number, message?: string) => {
  const configs = {
    200: {
      variant: "success" as const,
      message: message || "Success",
    },
    201: {
      variant: "success" as const,
      message: message || "Success",
    },
    204: {
      variant: "success" as const,
      message: message || "Success",
    },
    400: {
      variant: "error" as const,
      message: message || "Something went wrong. Please try again",
    },
    401: {
      variant: "error" as const,
      message: message || "Session expired. Please log in again",
    },
    403: {
      variant: "error" as const,
      message: message || "Access denied. You don't have permission to perform this action",
    },
    404: {
      variant: "error" as const,
      message: message || "Something went wrong. Please try again",
    },
    409: {
      variant: "warning" as const,
      message: message || "Aleady exists. Please check your data",
    },
    422: {
      variant: "error" as const,
      message: message || "Validation error. Please check your data",
    },
    423: {
      variant: "error" as const,
      message: message || "Resricted access. Please contact support",
    },
    429: {
      variant: "warning" as const,
      message: message || "Too many requests. Please try again later",
    },
    500: {
      variant: "error" as const,
      message: message || "Server error. Please try again",
    },
    502: {
      variant: "error" as const,
      message: message || "Service unavailable. Please try again",
    },
    503: {
      variant: "error" as const,
      message: message || "Service unavailable. Please try again later",
    },
    504: {
      variant: "error" as const,
      message: message || "Server timeout. Please try again",
    },
  };

  return (
    configs[status as keyof typeof configs] || {
      variant: "error" as const,
      // message: message || "An unexpected error occurred",
    }
  );
};

// Global toast function - will be set by the app
let globalToast: {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
} | null = null;

// Function to set the global toast instance
export const setGlobalToast = (toastInstance: typeof globalToast) => {
  globalToast = toastInstance;
};

// Helper function to show toast
const showToast = (
  status: number,
  message?: string,
  customMessage?: string,
) => {
  if (!globalToast) return;

  const config = getToastConfig(status, customMessage || message);
  const finalMessage = customMessage || message || config.message;

  globalToast[config.variant](finalMessage);
};

// Create axios instance
export const apiClient = axios.create({
  baseURL: "https://api.niveshhindustan.com/",
});

// Attach default headers
const attachAuthHeaders = () => {
  const token = localStorage.getItem("ExanGridToken");
  return isLoggedin() && token ? { Authorization: `Bearer ${token}` } : {};
};

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Show success toast for successful responses (optional)
    // You can customize this based on your needs
    const status = response.status;
    if (status >= 200 && status < 300) {
      // Only show success toast for certain operations
      const method = response.config.method?.toUpperCase();
      if (
        method === "POST" ||
        method === "PUT" ||
        method === "PATCH" ||
        method === "DELETE"
      ) {
        showToast(status);
      }
    }
    return response;
  },
  async (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const errorMessage = data?.message || data?.error || data?.detail;

      // Show error toast
      showToast(status, errorMessage);

      // Handle specific status codes
      switch (status) {
        case 401:
          // Clear token and redirect to login
          localStorage.removeItem("ExanGridToken");
          // You might want to redirect to login page here
          break;
        case 403:
          // Handle forbidden access
          break;
        case 404:
          // Handle not found
          break;
        case 422:
          // Handle validation errors
          break;
        case 500:
          // Handle server errors
          break;
        default:
          // Handle other errors
          break;
      }
    } else if (error.request) {
      // Network error - no response received
      showToast(0, "Network error. Please check your connection.");
    } else {
      // Request setup error
      showToast(0, "Request error. Please try again.");
    }

    return Promise.reject(error.response || error);
  },
);

// Fetcher object
const fetcher = {
  async request(
    method: string,
    endpoint: string,
    data: any = null,
    params: any = null,
    isFile: boolean = false,
    showSuccessToast: boolean = true,
    showErrorToast: boolean = true,
    customSuccessMessage?: string,
    customErrorMessage?: string,
  ) {
    const headers: any = attachAuthHeaders();
    if (isFile) headers["Content-Type"] = "multipart/form-data";

    try {
      const response = await apiClient.request({
        method,
        url: endpoint,
        data,
        params,
        headers,
      });

      // Show custom success message if provided
      if (showSuccessToast && customSuccessMessage) {
        showToast(response.status, undefined, customSuccessMessage);
      }

      return response;
    } catch (error: any) {
      // Show custom error message if provided
      if (showErrorToast && customErrorMessage) {
        showToast(error?.response?.status || 0, undefined, customErrorMessage);
      }
      throw error;
    }
  },

  get(
    endpoint: string,
    params = null,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "get",
      endpoint,
      null,
      params,
      false,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },

  post(
    endpoint: string,
    data: any,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "post",
      endpoint,
      data,
      null,
      false,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },

  patch(
    endpoint: string,
    data: any = null,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "patch",
      endpoint,
      data,
      null,
      false,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },

  delete(
    endpoint: string,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "delete",
      endpoint,
      null,
      null,
      false,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },

  put(
    endpoint: string,
    data: any,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "put",
      endpoint,
      data,
      null,
      false,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },

  postFile(
    endpoint: string,
    formData: FormData,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "post",
      endpoint,
      formData,
      null,
      true,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },

  putFile(
    endpoint: string,
    formData: FormData,
    options: {
      showSuccessToast?: boolean;
      showErrorToast?: boolean;
      customSuccessMessage?: string;
      customErrorMessage?: string;
    } = {},
  ) {
    return this.request(
      "put",
      endpoint,
      formData,
      null,
      true,
      options.showSuccessToast,
      options.showErrorToast,
      options.customSuccessMessage,
      options.customErrorMessage,
    );
  },
};

export default fetcher;
